import { useMoneyFormatter } from "@/lib/money-format";
import { useEffect, useMemo, useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { Banknote, Loader2, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TerceroLookup } from "@/components/shared";

import { TREASURY_STATUS_BADGE_VARIANT } from "@/components/treasury/format";
import { usePaymentVouchers } from "@/lib/api/payment-vouchers.queries";
import { useActiveCompany } from "@/lib/company-context";
import type { PaymentVoucherStatus } from "@/lib/api/payment-vouchers";
import type { TerceroSummary } from "@/lib/api/terceros";

export const Route = createFileRoute("/companies/$companyId/egresos/")({
  component: PaymentVouchersIndexPage,
});

function PaymentVouchersIndexPage() {
  return (
    <RequireAuth>
      <AppShell>
        <PaymentVouchersIndexContent />
      </AppShell>
    </RequireAuth>
  );
}

function PaymentVouchersIndexContent() {
  const formatCop = useMoneyFormatter();
  const { t } = useTranslation();
  const { companyId } = Route.useParams();
  const { setActiveCompanyId } = useActiveCompany();

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const [supplier, setSupplier] = useState<TerceroSummary | null>(null);
  const [status, setStatus] = useState<PaymentVoucherStatus | "all">("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);

  const filters = useMemo(
    () => ({
      ...(supplier ? { terceroId: supplier.id } : {}),
      ...(status !== "all" ? { status } : {}),
      ...(dateFrom ? { dateFrom } : {}),
      ...(dateTo ? { dateTo } : {}),
      page,
      pageSize: 50,
    }),
    [supplier, status, dateFrom, dateTo, page],
  );

  const listQuery = usePaymentVouchers(companyId, filters);
  const vouchers = listQuery.data?.vouchers ?? [];
  const total = listQuery.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / 50));
  const hasFilters = !!supplier || status !== "all" || !!dateFrom || !!dateTo;

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("vouchers.list.title")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("vouchers.list.subtitle")}
          </p>
        </div>
        <Button asChild>
          <Link to="/companies/$companyId/egresos/new" params={{ companyId }}>
            <Plus className="h-4 w-4" />
            {t("vouchers.list.new")}
          </Link>
        </Button>
      </div>

      <Card>
        <CardContent className="grid gap-3 pt-6 md:grid-cols-4">
          <div>
            <Label htmlFor="vouchers-supplier">
              {t("vouchers.list.filter_supplier")}
            </Label>
            <TerceroLookup
              id="vouchers-supplier"
              companyId={companyId}
              value={supplier}
              onChange={(next) => {
                setSupplier(next);
                setPage(1);
              }}
              allowCreate={false}
            />
          </div>
          <div>
            <Label htmlFor="vouchers-status">
              {t("vouchers.list.filter_status")}
            </Label>
            <Select
              value={status}
              onValueChange={(v) => {
                setStatus(v as PaymentVoucherStatus | "all");
                setPage(1);
              }}
            >
              <SelectTrigger id="vouchers-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("treasury.statusFilter.all")}</SelectItem>
                <SelectItem value="posted">{t("treasury.status.posted")}</SelectItem>
                <SelectItem value="void">{t("treasury.status.void")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="vouchers-from">{t("vouchers.list.filter_from")}</Label>
            <Input
              id="vouchers-from"
              type="date"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <div>
            <Label htmlFor="vouchers-to">{t("vouchers.list.filter_to")}</Label>
            <Input
              id="vouchers-to"
              type="date"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setPage(1);
              }}
            />
          </div>
        </CardContent>
      </Card>

      {listQuery.isPending ? (
        <div className="flex items-center justify-center rounded-lg border bg-card py-16">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : listQuery.isError ? (
        <ErrorState
          title={t("vouchers.list.error_title")}
          description={t("vouchers.list.error_body")}
          error={listQuery.error}
          onRetry={() => void listQuery.refetch()}
          retryLabel={t("common.retry")}
        />
      ) : vouchers.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-4 py-16 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
              <Banknote className="h-8 w-8 text-primary" />
            </div>
            <div className="max-w-sm space-y-1">
              <h2 className="text-lg font-semibold">
                {hasFilters
                  ? t("vouchers.list.empty_search_title")
                  : t("vouchers.list.empty_title")}
              </h2>
              <p className="text-sm text-muted-foreground">
                {hasFilters
                  ? t("vouchers.list.empty_search_body")
                  : t("vouchers.list.empty_body")}
              </p>
            </div>
            {!hasFilters ? (
              <Button asChild>
                <Link
                  to="/companies/$companyId/egresos/new"
                  params={{ companyId }}
                >
                  <Plus className="h-4 w-4" />
                  {t("vouchers.list.new")}
                </Link>
              </Button>
            ) : null}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("vouchers.table.number")}</TableHead>
                <TableHead>{t("vouchers.table.supplier")}</TableHead>
                <TableHead>{t("vouchers.table.date")}</TableHead>
                <TableHead className="text-right">
                  {t("vouchers.table.total")}
                </TableHead>
                <TableHead>{t("vouchers.table.concept")}</TableHead>
                <TableHead>{t("vouchers.table.status")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {vouchers.map((voucher) => (
                <TableRow key={voucher.id}>
                  <TableCell>
                    <Link
                      to="/companies/$companyId/egresos/$voucherId"
                      params={{ companyId, voucherId: voucher.id }}
                      className="font-mono text-sm font-medium text-primary hover:underline"
                    >
                      CE-{voucher.voucherNumber}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <p className="max-w-[200px] truncate text-sm">
                      {voucher.terceroName ?? "—"}
                    </p>
                  </TableCell>
                  <TableCell className="text-sm">{voucher.paymentDate}</TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    {formatCop(voucher.totalApplied)}
                  </TableCell>
                  <TableCell>
                    <p className="max-w-[180px] truncate text-xs text-muted-foreground">
                      {voucher.concept ?? t("vouchers.table.applications_mode")}
                    </p>
                  </TableCell>
                  <TableCell>
                    <Badge variant={TREASURY_STATUS_BADGE_VARIANT[voucher.status]}>
                      {t(`treasury.status.${voucher.status}`)}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {pageCount > 1 ? (
            <div className="flex items-center justify-between border-t px-4 py-3 text-sm">
              <span className="text-muted-foreground">
                {t("vouchers.list.page_of", { page, pageCount, total })}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  {t("common.back")}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= pageCount}
                  onClick={() => setPage((p) => p + 1)}
                >
                  {t("common.next")}
                </Button>
              </div>
            </div>
          ) : null}
        </Card>
      )}
    </div>
  );
}
